import { ArrowLeftOutlined } from '@ant-design/icons';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Flex, theme, Typography,
} from 'antd';
import {
    useCallback, useMemo, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate, useParams,
} from 'react-router-dom';

import {
    usersMutations, usersQueries,
} from '@/api';
import {
    ConfirmModal, getActionControlStyleVars, UserProjectsForm, UserProjectsFormRef,
} from '@/components';
import { AppRoute } from '@/constants';

export const UserProjectsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { token } = theme.useToken();
    const formRef = useRef<UserProjectsFormRef>(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [formData, setFormData] = useState<{
        selectedProjects: string[];
    } | null>(null);

    const { data: user } = useQuery(usersQueries.getOneUser(id || ''));

    const syncAccessMutationOptions = useMemo(
        () => usersMutations.syncAccess(id || ''),
        [id],
    );
    const { mutate: syncAccess, isPending } = useMutation(syncAccessMutationOptions);

    const handleSave = useCallback(() => {
        const data = formRef.current?.getFormData?.();
        if (data) {
            setFormData(data);
            setConfirmModalOpen(true);
        }
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData) {
            syncAccess(
                {
                    project_ids: formData.selectedProjects,
                },
                {
                    onSuccess: () => {
                        setConfirmModalOpen(false);
                        setFormData(null);
                        navigate(AppRoute.Users);
                    },
                },
            );
        }
    }, [formData, syncAccess, navigate]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    return (
        <>
            <Helmet>
                <title>Проекти та скрипти користувача</title>
            </Helmet>

            <Card
                title={
                    <Flex align="center" justify="space-between" gap="middle">
                        <Flex align="center" gap="middle">
                            <Link
                                to={AppRoute.Users}
                                aria-label="Назад"
                                className="app-action-control app-action-control--icon"
                                style={getActionControlStyleVars(token, {
                                    variant: 'text',
                                    bordered: false,
                                    iconOnly: true,
                                })}
                            >
                                <ArrowLeftOutlined style={{ fontSize: 16 }} />
                            </Link>
                            <Typography.Title level={4} style={{ margin: 0 }}>
                                Проекти та скрипти користувача
                            </Typography.Title>
                        </Flex>
                        <Flex gap="middle">
                            <Link
                                to={AppRoute.Users}
                                className="app-action-control"
                                style={getActionControlStyleVars(token)}
                            >
                                Скасувати
                            </Link>
                            <Button
                                type="primary"
                                onClick={handleSave}
                            >
                                Зберегти
                            </Button>
                        </Flex>
                    </Flex>
                }
            >
                {id && (
                    <UserProjectsForm
                        ref={formRef}
                        userId={id}
                        onSave={() => navigate(AppRoute.Users)}
                    />
                )}
            </Card>

            <ConfirmModal
                open={confirmModalOpen}
                mode="update"
                entityType="user"
                entityName={user?.nickname || user?.email}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />
        </>
    );
};
